import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, ArrowRight } from 'lucide-react';
import { BortleBadge } from './BortleBadge';
import type { LocationSummary as Location } from '@/lib/locations';
import { cn } from '@/lib/utils';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Props {
  location: Location;
  onSelect?: (location: Location) => void;
  isSelected?: boolean;
}

export const LocationCard: React.FC<Props> = ({ location, onSelect, isSelected }) => {
  const bestMonthsStr = location.best_months
    ?.slice(0, 3)
    .map((m) => MONTH_NAMES[m])
    .join(', ');

  const detailUrl = `/dark-sky-locations/${location.slug}`;

  const cardContent = (
    <Card className={cn(
      'overflow-hidden h-full hover:shadow-lg transition-shadow rounded-[4px]',
      isSelected && 'ring-2 ring-[#c9922a]/30 ring-offset-2 ring-offset-background border-[#c9922a]/40',
    )}>
      {/* Image or placeholder */}
      <div className="h-40 overflow-hidden bg-[#0f1923] relative">
        {location.featured_image_url ? (
          <img
            src={location.featured_image_url}
            alt={location.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-8 h-8 text-white/20" />
          </div>
        )}
        {location.bortle_class && (
          <div className="absolute top-2 right-2">
            <BortleBadge bortle={location.bortle_class} />
          </div>
        )}
      </div>

      <CardContent className="p-4 space-y-2">
        <h3 className="font-serif font-bold text-foreground text-lg leading-tight group-hover:text-[#c9922a] transition-colors line-clamp-2">
          {location.name}
        </h3>

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            {location.state_province ? `${location.state_province}, ` : ''}
            {location.country}
          </span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t text-sm">
          {bestMonthsStr && (
            <span className="text-muted-foreground">
              Best: {bestMonthsStr}
            </span>
          )}
          {/* The whole card is already the link to the detail page. A second <a> in
              here would nest links, which HTML can't hold: the parser splits the
              prerendered card apart and hydration fails (ERI-2837). */}
          {!onSelect && (
            <span className="text-[#c9922a] hover:underline flex items-center gap-1 text-xs font-medium ml-auto">
              Plan a shoot <ArrowRight className="w-3 h-3" />
            </span>
          )}
          {onSelect && (
            <span className={cn(
              'flex items-center gap-1 text-xs font-medium ml-auto transition-colors',
              isSelected ? 'text-[#c9922a]' : 'text-muted-foreground/60 group-hover:text-[#c9922a]',
            )}>
              {isSelected ? 'Selected' : 'Select'} <ArrowRight className="w-3 h-3" />
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (onSelect) {
    return (
      <button
        onClick={() => onSelect(location)}
        className="group text-left w-full"
      >
        {cardContent}
      </button>
    );
  }

  return (
    <Link to={detailUrl} className="group">
      {cardContent}
    </Link>
  );
};
